import React from 'react';

function MultiFileEditsContent() {
  return (
    <>
      <p>RepoPrompt excels at handling code changes that span multiple files, providing a streamlined workflow for reviewing and applying complex edits.</p>
      
      <h3>Understanding Multi-File Edits</h3>
      <p>When an AI suggests changes that affect multiple files, RepoPrompt:</p>
      <ul>
        <li>Organizes the changes by file for easy navigation</li>
        <li>Provides a comprehensive diff view for each affected file</li>
        <li>Allows you to accept or reject changes on a per-file basis</li>
        <li>Maintains the integrity of your codebase during the process</li>
      </ul>
      
      <h3>The Diff Review Interface</h3>
      <p>After receiving suggestions (via integrated chat or Apply mode), the diff review interface shows:</p>
      <ul>
        <li><strong>File List:</strong> All affected files, organized by change type (edit, create, delete)</li>
        <li><strong>Diff View:</strong> Side-by-side comparison of original and modified code</li>
        <li><strong>Line Numbers:</strong> For precise identification of changes</li>
        <li><strong>Highlighting:</strong> Added lines (green), removed lines (red), and unchanged context</li>
      </ul>
      
      <h3>Reviewing Changes</h3>
      <ol>
        <li>When changes are proposed, click "Merge Changes" to enter the review interface</li>
        <li>Navigate between files using the file list on the left</li>
        <li>For each file, carefully review the highlighted changes</li>
        <li>Use the checkboxes or buttons to accept or reject changes for individual files</li>
      </ol>
      
      <h3>Applying Changes</h3>
      <p>After reviewing the proposed edits, you have several options:</p>
      <ul>
        <li><strong>Accept All Files:</strong> Apply all changes across all files</li>
        <li><strong>Accept Individual Files:</strong> Apply changes only to specific files</li>
        <li><strong>Reset All Files:</strong> Discard all proposed changes</li>
        <li><strong>Exit Review:</strong> Close the diff view and return to the previous screen</li>
      </ul>
      
      <h3>Working with File Operations</h3>
      <p>RepoPrompt handles different types of file operations:</p>
      <ul>
        <li><strong>Edits:</strong> Modifications to existing files</li>
        <li><strong>Creations:</strong> Entirely new files added to your project</li>
        <li><strong>Deletions:</strong> Removal of existing files</li>
      </ul>
      
      <h3>Safety Features</h3>
      <p>To protect your code, RepoPrompt provides several safety mechanisms:</p>
      <ul>
        <li>Changes are only applied after explicit confirmation</li>
        <li>Individual file acceptance lets you apply only the changes you want</li>
        <li>The diff view clearly highlights all modifications for verification</li>
        <li>Hitting reset on a file or all files restores the file to the state before the changes were applied</li>
      </ul>
      
      <div className="tip-box">
        <strong>Tip:</strong> For complex refactoring operations, review changes in logical order rather than alphabetically. Start with core components or interfaces, then move to implementations to ensure everything remains consistent.
      </div>
    </>
  );
}

export default MultiFileEditsContent;